// ---------- Utility helpers ----------

const normalizeDateMidnight = (d) => {
  const dt = new Date(d);
  dt.setHours(0,0,0,0);
  return dt;
};

const formatMonth = (year, month) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[month];
};

// ---------- Main function (month-based validation) ----------

export function validateAddressCoverage(addresses = [], taxYears = []) {
  // If no tax years provided, validation passes
  if (!taxYears || taxYears.length === 0) {
    return { isValid: true, missingYears: [], errors: [], missingRanges: [] };
  }

  const addressesArray = Array.isArray(addresses) ? addresses : [];

  // if there are no addresses, then each year is fully missing
  if (addressesArray.length === 0) {
    const missingYears = taxYears
      .map(y => (typeof y === 'string' ? parseInt(y,10) : y))
      .filter(y => !isNaN(y));
    const allMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const missingRanges = missingYears.map(y => ({
      year: y,
      monthList: allMonths.join(', ') // All months are missing
    }));
    return {
      isValid: false,
      missingYears,
      errors: missingYears.map(y => `Address coverage required for tax year ${y}.`),
      missingRanges
    };
  }

  const missingYears = [];
  const errors = [];
  const missingRanges = [];

  for (const yRaw of taxYears) {
    const year = typeof yRaw === 'string' ? parseInt(yRaw,10) : yRaw;
    if (isNaN(year)) continue;

    // Track which months are covered (0-11 for Jan-Dec)
    const coveredMonths = new Set();

    // Check each address to see which months it covers
    for (const a of addressesArray) {
      if (!a || !a.fromDate || !a.toDate) continue;
      
      try {
        const from = normalizeDateMidnight(a.fromDate);
        const to = normalizeDateMidnight(a.toDate);

        if (isNaN(from.getTime()) || isNaN(to.getTime())) continue;

        // Get the year, month of from and to dates
        const fromYear = from.getFullYear();
        const fromMonth = from.getMonth();
        const toYear = to.getFullYear();
        const toMonth = to.getMonth();

        // If address overlaps with this tax year, mark months as covered
        if (fromYear <= year && toYear >= year) {
          // Determine which months in this year are covered
          const startMonth = fromYear === year ? fromMonth : 0;
          const endMonth = toYear === year ? toMonth : 11;
          
          // Mark all months from startMonth to endMonth as covered
          for (let m = startMonth; m <= endMonth; m++) {
            coveredMonths.add(m);
          }
        }
      } catch (e) {
        continue;
      }
    }

    // Check which months are missing (0-11 for Jan-Dec)
    const missingMonths = [];
    for (let m = 0; m < 12; m++) {
      if (!coveredMonths.has(m)) {
        missingMonths.push(m);
      }
    }

    // If there are missing months, the year is not fully covered
    if (missingMonths.length > 0) {
      missingYears.push(year);
      
      // Group consecutive months into ranges
      const monthGroups = [];
      let groupStart = missingMonths[0];
      let groupEnd = missingMonths[0];

      for (let i = 1; i < missingMonths.length; i++) {
        if (missingMonths[i] === groupEnd + 1) {
          // Consecutive month - extend range
          groupEnd = missingMonths[i];
        } else {
          // Gap - save current group and start new one
          monthGroups.push({ start: groupStart, end: groupEnd });
          groupStart = missingMonths[i];
          groupEnd = missingMonths[i];
        }
      }
      // Add the last group
      monthGroups.push({ start: groupStart, end: groupEnd });

      // Format groups: single month as "Jan", range as "Jan - Apr"
      const monthListParts = monthGroups.map(group => {
        const startName = formatMonth(year, group.start);
        if (group.start === group.end) {
          return startName; // Single month
        } else {
          const endName = formatMonth(year, group.end);
          return `${startName} - ${endName}`; // Range
        }
      });

      const monthList = monthListParts.join(', ');

      missingRanges.push({
        year,
        monthList // Formatted list with ranges and individual months
      });

      if (errors.length === 0) {
        errors.push(`Tax year ${year} is not fully covered by addresses.`);
      } else {
        errors.push(`Tax year ${year} also needs full coverage.`);
      }
    }
  }

  return {
    isValid: missingYears.length === 0,
    missingYears,
    errors,
    missingRanges
  };
}

// ---------- dateRangeCoversYear utility (kept for compatibility) ----------

export function dateRangeCoversYear(fromDate, toDate, year) {
  if (!fromDate || !toDate) return false;
  const f = normalizeDateMidnight(fromDate);
  const t = normalizeDateMidnight(toDate);
  const fromYear = f.getFullYear();
  const toYear = t.getFullYear();
  // covered if the address range overlaps with the tax year at all
  return fromYear <= year && toYear >= year;
}

